import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TFunction } from '@lyearn/i18n';
import { Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Stack } from '@lyearn/ui';
import TagsAndProperties from '@/components/FilterManager/Tags/TagsAndProperties';
import { SystemProperty } from '@/components/FilterManager/Tags/TagsAndProperties/types';
import {
  FilterOperatorEnum,
  FilterPropertyTypeEnum,
  GroupTypeEnum,
  TagTypeEnum,
  TagUnion,
} from '@/types/schema';

import {
  EMPTY_FILTER,
  RESET_VALUE_FOR_OPERATORS,
  VALUE_DISABLED_FOR_OPERATORS,
} from '../../consts';
import { tagsAndUserAutocompleteClasses } from '../../styles';
import { FilterInputsD2 } from '../../types';
import FilterValues from '../FilterValues';
import Loader from '../Loader';
import PropertiesOperator from '../PropertiesOperator';
import { getOperators } from '../PropertiesOperator/utils';

export type FilterItemOverrides = {
  getOperators?: typeof getOperators;
  defaultValue?: string;
  disableDateValueTypeSelector?: boolean;
  tagTypes?: TagTypeEnum[];
  allowedFilterTypes?: FilterPropertyTypeEnum[];
  allowedProperties?: SystemProperty[];
};

export interface FilterItemProps {
  onChange: (item: FilterInputsD2, index: number) => void;
  onRemoveFilter: (index: number) => void;
  index: number;
  filter: FilterInputsD2;
  error?: boolean;
  type: GroupTypeEnum;
  overrides?: FilterItemOverrides;
  disabled?: boolean;
  className?: string;
  t?: TFunction;
  showPlaceholderIcon?: boolean;
  order?: number;
}

function FilterItem(props: FilterItemProps) {
  const { t } = useTranslation();
  const {
    onRemoveFilter,
    index,
    onChange,
    filter,
    error,
    type,
    overrides,
    className,
    showPlaceholderIcon,
  } = props;
  const getOperatorsFn = overrides?.getOperators ?? getOperators;

  const onRemove = useCallback(() => onRemoveFilter(index), [index, onRemoveFilter]);
  const onPropertyChange = useCallback(
    (params?: { value: string; propertyType: FilterPropertyTypeEnum; tag?: TagUnion }) => {
      if (!params) {
        onChange(EMPTY_FILTER, index);
        return;
      }

      const { value, propertyType, tag } = params;
      const operators = getOperatorsFn(t, propertyType, tag?.type, value);
      onChange(
        {
          ...filter,
          property: {
            type: propertyType,
            value,
            tag,
          },
          operator: operators[0].id,
          value: overrides?.defaultValue,
        },
        index,
      );
    },
    [filter, getOperatorsFn, index, onChange, overrides?.defaultValue, t],
  );

  const onOperatorChange = useCallback(
    (operator: FilterOperatorEnum) => {
      let newValue = filter.value;
      if (RESET_VALUE_FOR_OPERATORS.includes(operator)) {
        newValue = '';
      } else if (VALUE_DISABLED_FOR_OPERATORS.includes(operator)) {
        newValue = '';
      }
      onChange(
        {
          ...filter,
          operator,
          value: newValue,
        },
        index,
      );
    },
    [filter, index, onChange],
  );

  const onFilterValueChange = useCallback(
    (value?: string) => {
      onChange(
        {
          ...filter,
          value,
        },
        index,
      );
    },
    [filter, index, onChange],
  );

  const operators = getOperatorsFn(
    t,
    filter.property?.type,
    filter.property?.tag?.type,
    filter.property?.value,
  );

  return (
    <Stack alignItems="start" className="mb-12 w-full" direction="col">
      <Stack alignItems="start" className={cn('w-full', className)} gapX={8}>
        <TagsAndProperties
          Loader={<Loader />}
          allowedFilterTypes={overrides?.allowedFilterTypes}
          allowedProperties={overrides?.allowedProperties}
          classes={tagsAndUserAutocompleteClasses}
          selectedProperty={filter.property?.value}
          showPlaceholderIcon={showPlaceholderIcon}
          tagTypes={overrides?.tagTypes}
          type={type}
          onChange={onPropertyChange}
        />
        <PropertiesOperator
          operators={operators}
          propertyType={filter.property?.type}
          selectedOperator={filter.operator}
          onOperatorChange={onOperatorChange}
        />
        <FilterValues
          key={filter.operator}
          disableDateValueTypeSelector={overrides?.disableDateValueTypeSelector}
          disabled={!filter.operator || VALUE_DISABLED_FOR_OPERATORS.includes(filter.operator)}
          error={error}
          filterValue={filter.value}
          operator={filter.operator}
          propertyType={filter.property?.type}
          propertyValue={filter.property?.value}
          tag={filter.property?.tag}
          onFilterValueChange={onFilterValueChange}
        />
        <LinkButton className="flex-shrink-0 py-2 pt-10" color={'internal'} onClick={onRemove}>
          <Close />
        </LinkButton>
      </Stack>
    </Stack>
  );
}

export default FilterItem;
